import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Stack,
  Button,
  Chip,
  Avatar,
  Divider,
  Paper,
  Alert,
  CircularProgress,
  Fade,
  Zoom,
  Modal,
  IconButton
} from '@mui/material';
import {
  Business as BusinessIcon,
  LocationOn as LocationOnIcon,
  Description as DescriptionIcon,
  VideoLibrary as VideoLibraryIcon,
  PhotoLibrary as PhotoLibraryIcon,
  AttachMoney as AttachMoneyIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Pending as PendingIcon,
  Download as DownloadIcon,
  ArrowBack as ArrowBackIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
  Star as StarIcon,
  Group as GroupIcon,
  Link as LinkIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { GetAll, apiURL } from '../../../utils/apiCalls';
import { toast } from 'react-toastify';

const ProcurementLayoutRequestView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);

  const fetchRequestDetails = useCallback(async () => {
    setLoading(true);
    try {
      const response = await GetAll(apiURL + `/api/v1/layouts/${id}`);
      if (response && response.data && response.data.status) {
        setRequest(response.data.data);
      } else {
        toast.error('Failed to fetch request details');
        navigate('/dashboard/procurement/layout-requests');
      }
    } catch (error) {
      console.error('Error fetching request details:', error);
      toast.error('An error occurred while fetching request details');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchRequestDetails();
  }, [fetchRequestDetails]);

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved': return 'success';
      case 'rejected': return 'error';
      case 'pending': return 'warning';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved': return <CheckCircleIcon />;
      case 'rejected': return <CancelIcon />;
      case 'pending': return <PendingIcon />;
      default: return null;
    }
  };

  const handlePreviewFile = (doc) => {
    setPreviewFile(doc);
    setPreviewModalOpen(true);
  };

  const handleDownloadFile = (doc) => {
    const link = document.createElement('a');
    link.href = doc.s3_url;
    link.download = doc.original_filename || doc.document_type;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleClosePreview = () => {
    setPreviewModalOpen(false);
    setPreviewFile(null);
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress size={64} />
        </Box>
      </Container>
    );
  }

  if (!request) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">Request not found</Alert>
      </Container>
    );
  }

  return (
    <Container sx={{ m: 0, width: '100%', maxWidth: '100%' }} maxWidth={false}>
      {/* Header */}
      <Fade in timeout={800}>
        <Box sx={{ mb: 4 }}>
          <Stack direction="row" alignItems="center" spacing={3} sx={{ mb: 3 }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/dashboard/procurement/layout-requests')}
              sx={{ mr: 2 }}
            >
              Back
            </Button>
            <Avatar sx={{
              bgcolor: '#1976d2',
              width: 64,
              height: 64,
              boxShadow: '0 8px 16px rgba(25, 118, 210, 0.3)'
            }}>
              <BusinessIcon sx={{ fontSize: 32, color: 'white' }} />
            </Avatar>
            <Box>
              <Typography variant="h4" component="h1" fontWeight="bold" color="text.primary">
                Layout Request Details
              </Typography>
              <Typography variant="h6" color="text.secondary">
                {request.venture_name}
              </Typography>
            </Box>
          </Stack>
          <Divider sx={{ my: 2 }} />
        </Box>
      </Fade>

      <Grid container spacing={4}>
        {/* Request Details */}
        <Grid item xs={12} md={8}>
          <Stack spacing={4}>
            {/* Venture Information */}
            <Zoom in timeout={1000}>
              <Card>
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                    <BusinessIcon color="primary" />
                    <Typography variant="h6" fontWeight="bold">
                      Venture Information
                    </Typography>
                  </Stack>
                  <Grid container spacing={3}>
                   { request.project_name &&      <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Project Name</Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {request.project_name}
                      </Typography>
                    </Grid>}
                     { request.company_name && 
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Company Name</Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {request.company_name}
                      </Typography>
                    </Grid>}


                { request.project_location &&   <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Project Location </Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {request.project_location}
                      </Typography>
                    </Grid>}

{ request.highways &&
                           <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">On which highway is this project located?</Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {request.highways}
                      </Typography>
                    </Grid>}

{ request.is_own_land !=null&&
                           <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Is this the company's own land?</Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {request.is_own_land ? "Yes" : "No"}
                      </Typography>
                    </Grid>
}

{ request.is_own_venture != null &&
                           <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Is this a company-owned venture?</Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {request.is_own_venture? "Yes" : "No"}
                      </Typography>
                    </Grid>
}

{ request.nala_conversion != null &&
                           <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Will you do NALA conversion?</Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {request.nala_conversion? "Yes" : "No"}
                      </Typography>
                    </Grid>
}

{ request.registrations &&
                           <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">How many people will be involved in this land registration?</Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {request.registrations}
                      </Typography>
                    </Grid>
}

{ request.development_progress &&
                           <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">How far has the development progressed?</Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {request.development_progress}
                      </Typography>
                    </Grid>
}

{ request.bank_loan != null &&
                           <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Will I get a bank loan?</Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {request.bank_loan? "Yes" : "No"}
                      </Typography>
                    </Grid>
}


{ request.project_type  &&
                           <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Project Type</Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {request.project_type}
                      </Typography>
                    </Grid>
}



{ request.rera_number &&
                           <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">RERA Number</Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {request.rera_number}
                      </Typography>
                    </Grid>
}


{ request.mortgage_gauge_plots != null &&
                           <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Are there any mortgage plots?</Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {request.mortgage_gauge_plots? "Yes" : "No"}
                      </Typography>
                    </Grid>
}



{ request.hydra_rules != null &&
                           <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Are there hydra rules?</Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {request.hydra_rules? "Yes" : "No"}
                      </Typography>
                    </Grid>
}



{ request.entry_timing  &&
                           <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">When can you enter the house?</Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {request.entry_timing}
                      </Typography>
                    </Grid>
}

{ request.oc  &&
                           <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Did the OC come?</Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {request.oc}
                      </Typography>
                    </Grid>
}

{ request.project_age  &&
                           <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Project Age</Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {request.project_age}
                      </Typography>
                    </Grid>
}

{ request.legal_opinion  &&
                           <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Legal Opinion</Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {request.legal_opinion}
                      </Typography>
                    </Grid>
}

{ request.oc_arrived != null &&
                           <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Has OC Arrived?</Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {request.oc_arrived? "Yes" : "No"}
                      </Typography>
                    </Grid>
}



                  </Grid>





                  
                </CardContent>
              </Card>
            </Zoom>

            {/* Location Information */}
            <Zoom in timeout={1200}>
              <Card>
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                    <LocationOnIcon color="primary" />
                    <Typography variant="h6" fontWeight="bold">
                      Location Information
                    </Typography>
                  </Stack>
                  <Grid container spacing={3}>
                    {request.location_latitude && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">Latitude</Typography>
                        <Typography variant="body1" fontWeight="bold">
                          {request.location_latitude}
                        </Typography>
                      </Grid>
                    )}
                    {request.location_longitude && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">Longitude</Typography>
                        <Typography variant="body1" fontWeight="bold">
                          {request.location_longitude}
                        </Typography>
                      </Grid>
                    )}
                    {request.google_map_link && (
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Google Map Link</Typography>
                        <Button
                          variant="outlined"
                          startIcon={<LinkIcon />}
                          href={request.google_map_link}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Open in Google Maps
                        </Button>
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>
            </Zoom>

            {/* Legal Documents */}
            <Zoom in timeout={1400}>
              <Card>
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                    <DescriptionIcon color="primary" />
                    <Typography variant="h6" fontWeight="bold">
                      Legal Documents
                    </Typography>
                  </Stack>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="body2" color="text.secondary">LP Number</Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {request.lp_number}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="body2" color="text.secondary">HMDA Letter</Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {request.hmda_letter}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="body2" color="text.secondary">REPA Number</Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {request.repa_number}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Zoom>

            {/* Pricing Information */}
            {(request.price_per_sq_yard || request.price_per_sft || request.corner_charges_ne || request.corner_charges_nw || request.facing_charges_east || request.facing_charges_north || request.facing_charges_west) && (
              <Zoom in timeout={1600}>
                <Card>
                  <CardContent>
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                      <AttachMoneyIcon color="primary" />
                      <Typography variant="h6" fontWeight="bold">
                        Pricing Information
                      </Typography>
                    </Stack>
                    <Grid container spacing={3}>
                      {request.price_per_sq_yard && (
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="text.secondary">Price per Sq Yard</Typography>
                          <Typography variant="body1" fontWeight="bold">
                            ₹{request.price_per_sq_yard}
                          </Typography>
                        </Grid>
                      )}
                      {request.price_per_sft && (
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="text.secondary">Price per Sft</Typography>
                          <Typography variant="body1" fontWeight="bold">
                            ₹{request.price_per_sft}
                          </Typography>
                        </Grid>
                      )}
                      {request.corner_charges_ne && (
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="text.secondary">Corner Charges (North-East)</Typography>
                          <Typography variant="body1" fontWeight="bold">
                            ₹{request.corner_charges_ne}
                          </Typography>
                        </Grid>
                      )}
                      {request.corner_charges_nw && (
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="text.secondary">Corner Charges (North-West)</Typography>
                          <Typography variant="body1" fontWeight="bold">
                            ₹{request.corner_charges_nw}
                          </Typography>
                        </Grid>
                      )}
                      {request.facing_charges_east && (
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="text.secondary">Facing Charges (East)</Typography>
                          <Typography variant="body1" fontWeight="bold">
                            ₹{request.facing_charges_east}
                          </Typography>
                        </Grid>
                      )}
                      {request.facing_charges_north && (
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="text.secondary">Facing Charges (North)</Typography>
                          <Typography variant="body1" fontWeight="bold">
                            ₹{request.facing_charges_north}
                          </Typography>
                        </Grid>
                      )}
                      {request.facing_charges_west && (
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2" color="text.secondary">Facing Charges (West)</Typography>
                          <Typography variant="body1" fontWeight="bold">
                            ₹{request.facing_charges_west}
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                  </CardContent>
                </Card>
              </Zoom>
            )}

            {/* Commission Information */}
            <Zoom in timeout={1700}>
              <Card>
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                    <AttachMoneyIcon color="primary" />
                    <Typography variant="h6" fontWeight="bold">
                      Commission Information
                    </Typography>
                  </Stack>
                  <Grid container spacing={3}>
                    {request.associate_highest_commission && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">Associate Highest Commission</Typography>
                        <Typography variant="body1" fontWeight="bold">
                          {request.associate_highest_commission}%
                        </Typography>
                      </Grid>
                    )}
                    {request.plp_margin && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="text.secondary">PLP Margin</Typography>
                        <Typography variant="body1" fontWeight="bold">
                          {request.plp_margin}%
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>
            </Zoom>

            {/* Project Highlights */}
            {request.project_highlights && (
              <Zoom in timeout={1800}>
                <Card>
                  <CardContent>
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                      <StarIcon color="primary" />
                      <Typography variant="h6" fontWeight="bold">
                        Project Highlights
                      </Typography>
                    </Stack>
                    <Box component="ul" sx={{ pl: 3, m: 0 }}>
                      {request.project_highlights.split('\n').filter(line => line.trim()).map((highlight, index) => (
                        <Typography key={index} component="li" variant="body1" sx={{ mb: 1 }}>
                          {highlight.trim()}
                        </Typography>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Zoom>
            )}

            {/* Amenities */}
            {request.amenities && (
              <Zoom in timeout={1900}>
                <Card>
                  <CardContent>
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                      <StarIcon color="primary" />
                      <Typography variant="h6" fontWeight="bold">
                        Amenities
                      </Typography>
                    </Stack>
                    <Box component="ul" sx={{ pl: 3, m: 0 }}>
                      {request.amenities.split('\n').filter(line => line.trim()).map((amenity, index) => (
                        <Typography key={index} component="li" variant="body1" sx={{ mb: 1 }}>
                          {amenity.trim()}
                        </Typography>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Zoom>
            )}

            {/* Procurement Partners */}
            {request.procurement_partners && (
              <Zoom in timeout={2000}>
                <Card>
                  <CardContent>
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                      <GroupIcon color="primary" />
                      <Typography variant="h6" fontWeight="bold">
                        Procurement Partners
                      </Typography>
                    </Stack>
                    {(() => {
                      try {
                        const partners = typeof request.procurement_partners === 'string' 
                          ? JSON.parse(request.procurement_partners) 
                          : request.procurement_partners;
                        const partnersArray = Array.isArray(partners) ? partners : [];
                        
                        return (
                          <Grid container spacing={2}>
                            <Grid item xs={12}>
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                Total Partners: {request.procurement_partners_count || partnersArray.length}
                              </Typography>
                            </Grid>
                            {partnersArray.map((partner, index) => (
                              <Grid item xs={12} sm={6} key={index}>
                                <Paper elevation={1} sx={{ p: 2, bgcolor: partner.is_primary ? 'primary.50' : 'grey.50' }}>
                                  <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                                    PP{index + 1} {partner.is_primary && '(Primary)'}
                                  </Typography>
                                  {partner.admin_id && (
                                    <Typography variant="body2" color="text.secondary">
                                      Admin ID: {partner.admin_id}
                                    </Typography>
                                  )}
                                  <Typography variant="body1" fontWeight="bold" color="primary">
                                    Commission: {partner.commission_percentage || 0}%
                                  </Typography>
                                </Paper>
                              </Grid>
                            ))}
                            {partnersArray.length > 0 && (
                              <Grid item xs={12}>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                  Total Commission: {partnersArray.reduce((sum, p) => sum + (parseFloat(p.commission_percentage) || 0), 0)}%
                                </Typography>
                              </Grid>
                            )}
                          </Grid>
                        );
                      } catch (e) {
                        return (
                          <Typography variant="body2" color="text.secondary">
                            Unable to parse procurement partners data
                          </Typography>
                        );
                      }
                    })()}
                  </CardContent>
                </Card>
              </Zoom>
            )}

            {/* Documents and Media */}
            <Zoom in timeout={2100}>
              <Card>
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                    <PhotoLibraryIcon color="primary" />
                    <Typography variant="h6" fontWeight="bold">
                      Documents and Media
                    </Typography>
                  </Stack>
                  <Grid container spacing={2}>
                    {request.documents && request.documents.length > 0 ? (
                      request.documents.map((doc, index) => (
                        <Grid item xs={12} sm={6} md={4} key={doc.id || index}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Button
                              variant="outlined"
                              startIcon={
                                doc.mime_type?.startsWith('video/') ? (
                                  <VideoLibraryIcon />
                                ) : doc.mime_type?.startsWith('image/') ? (
                                  <PhotoLibraryIcon />
                                ) : (
                                  <DownloadIcon />
                                )
                              }
                              fullWidth
                              onClick={() => handleDownloadFile(doc)}
                              sx={{ 
                                flexDirection: 'column', 
                                height: 'auto', 
                                py: 2,
                                textAlign: 'left'
                              }}
                            >
                              <Typography variant="body2" fontWeight="bold" sx={{ mb: 0.5 }}>
                                {doc.sub_category ? 
                                  doc.sub_category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : 
                                  doc.document_type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
                                }
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {(doc.file_size / 1024 / 1024).toFixed(2)} MB
                              </Typography>
                            </Button>
                            <Stack direction="row" spacing={1}>
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<VisibilityIcon />}
                                onClick={() => handlePreviewFile(doc)}
                                sx={{ flex: 1 }}
                              >
                                Preview
                              </Button>
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<DownloadIcon />}
                                onClick={() => handleDownloadFile(doc)}
                                sx={{ flex: 1 }}
                              >
                                Download
                              </Button>
                            </Stack>
                          </Box>
                        </Grid>
                      ))
                    ) : (
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary" align="center">
                          No documents uploaded
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>
            </Zoom>
          </Stack>
        </Grid>

        {/* Status Panel */}
        <Grid item xs={12} md={4}>
          <Zoom in timeout={2000}>
            <Paper elevation={3} sx={{ p: 3, position: 'sticky', top: 20 }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Request Status
              </Typography>
              <Chip
                label={request.status || 'Pending'}
                color={getStatusColor(request.status)}
                icon={getStatusIcon(request.status)}
                sx={{ mb: 3, fontWeight: 'bold' }}
              />

              <Divider sx={{ my: 3 }} />

              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Request Information
              </Typography>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="body2" color="text.secondary">Request ID</Typography>
                  <Typography variant="body1" fontWeight="bold">
                    #{request.id}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">Submitted On</Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {new Date(request.created_at).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </Typography>
                </Box>
                {request.updated_at && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">Last Updated</Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {new Date(request.updated_at).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </Typography>
                  </Box>
                )}
              </Stack>

              {request.admin_notes && (
                <>
                  <Divider sx={{ my: 3 }} />
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Admin Notes
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {request.admin_notes}
                  </Typography>
                </>
              )}

              {request.comments && (
                <>
                  <Divider sx={{ my: 3 }} />
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Comments
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {request.comments}
                  </Typography>
                </>
              )}
            </Paper>
          </Zoom>
        </Grid>
      </Grid>

      {/* File Preview Modal */}
      <Modal open={previewModalOpen} onClose={handleClosePreview}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '90vw',
            height: '90vh',
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            p: 2,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Modal Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" fontWeight="bold">
              File Preview - {previewFile?.sub_category ? 
                previewFile.sub_category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : 
                previewFile?.document_type || 'Document'
              }
            </Typography>
            <IconButton onClick={handleClosePreview} size="small">
              <CloseIcon />
            </IconButton>
          </Box>

          {/* File Info */}
          {previewFile && (
            <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Typography variant="body2" color="text.secondary">
                  <strong>File Type:</strong> {previewFile.mime_type}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Size:</strong> {(previewFile.file_size / 1024 / 1024).toFixed(2)} MB
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<DownloadIcon />}
                  onClick={() => handleDownloadFile(previewFile)}
                >
                  Download
                </Button>
              </Stack>
            </Box>
          )}

          {/* File Preview Content */}
          <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
            {previewFile && (
              <>
                {previewFile.mime_type?.startsWith('image/') ? (
                  <Box
                    component="img"
                    src={previewFile.s3_url}
                    alt="Preview"
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                      borderRadius: 1,
                    }}
                  />
                ) : previewFile.mime_type?.startsWith('video/') ? (
                  <Box
                    component="video"
                    src={previewFile.s3_url}
                    controls
                    sx={{
                      width: '100%',
                      height: '100%',
                      borderRadius: 1,
                    }}
                  />
                ) : previewFile.mime_type === 'application/pdf' ? (
                  <Box
                    component="iframe"
                    src={previewFile.s3_url}
                    sx={{
                      width: '100%',
                      height: '100%',
                      border: 'none',
                      borderRadius: 1,
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '100%',
                      textAlign: 'center',
                    }}
                  >
                    <DownloadIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      Preview not available
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      This file type cannot be previewed in the browser
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<DownloadIcon />}
                      onClick={() => handleDownloadFile(previewFile)}
                    >
                      Download to view
                    </Button>
                  </Box>
                )}
              </>
            )}
          </Box>
        </Box>
      </Modal>
    </Container>
  );
};

export default ProcurementLayoutRequestView;
